import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import DashboardScopeBar from "../components/dashboard/DashboardScopeBar";
import { PeriodKpiGrid, CurrentKpiGrid } from "../components/dashboard/DashboardKpiGrid";
import DashboardStatusPanel from "../components/dashboard/DashboardStatusPanel";
import DashboardByTeam from "../components/dashboard/DashboardByTeam";
import DashboardWorkload from "../components/dashboard/DashboardWorkload";
import DashboardFunnel from "../components/dashboard/DashboardFunnel";
import DashboardAcceptedTrend from "../components/dashboard/DashboardAcceptedTrend";
import DashboardAging from "../components/dashboard/DashboardAging";
import DashboardByOwner from "../components/dashboard/DashboardByOwner";
import { bucketEndSofia, computePreset, fmtSofiaDate } from "../lib/dashboardDates";
import { buildClickThroughParams } from "../lib/dashboardMetric";
import { ownQuotationsOnly } from "../lib/quotationVisibility";
import { useDashboardData } from "../hooks/useDashboardData";
import { useDashboardScope } from "../hooks/useDashboardScope";
import { useVisibleInterval } from "../hooks/useVisibleInterval";
import { friendlyError } from "../lib/errorReporter";

const REFRESH_MS = 60_000;
const DEFAULT_PRESET_ID = "last_30";
const UNASSIGNED = "unassigned";

function ChartSkeleton() {
  return <div className="qw-skel h-280 stretch" aria-busy="true" />;
}

function SectionTitle({ title, note, testid }) {
  return (
    <div className="qw-section-head" data-testid={testid}>
      <span className="qw-section-title">{title}</span>
      {note ? <span className="qw-section-note">{note}</span> : null}
    </div>
  );
}

/** Convert URL owner_id + mine params into a single logical value. */
function readOwnerFromUrl(searchParams) {
  const oid = searchParams.get("owner_id");
  const mine = searchParams.get("mine") === "true";
  if (oid === UNASSIGNED) return UNASSIGNED;
  if (oid) return oid;
  if (mine) return "mine";
  return null;
}

/** The team scope from the URL: null (all), "unassigned" (Без екип) or a UUID. */
function readTeamFromUrl(searchParams) {
  const tid = searchParams.get("team_id");
  if (!tid) return null;
  return tid;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { orgId, user, currentOrg } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Every number on this screen is already scoped to the actor server-side, so
  // a cross-user filter, a team comparison and a per-owner comparison have
  // nothing to show for a self-scoped role.
  const ownOnly = ownQuotationsOnly(currentOrg?.role);

  const urlFrom = searchParams.get("from");
  const urlTo = searchParams.get("to");
  const ownerLogical = readOwnerFromUrl(searchParams);
  const urlTeam = readTeamFromUrl(searchParams);

  const { scope } = useDashboardScope(orgId, { enabled: !ownOnly });

  // A Manager reads exactly the team it manages: the scope is server-resolved
  // and read-only, so a team_id in the URL can never redirect it (and a
  // hand-made request naming another team is refused with 403 server-side).
  const teamLogical = useMemo(() => {
    if (ownOnly) return null;
    if (scope.team_locked) return scope.locked_team_id || null;
    return urlTeam;
  }, [ownOnly, scope.team_locked, scope.locked_team_id, urlTeam]);

  const range = useMemo(() => {
    if (urlFrom && urlTo) return { from: urlFrom, to: urlTo };
    return computePreset(DEFAULT_PRESET_ID);
  }, [urlFrom, urlTo]);

  const [customFrom, setCustomFrom] = useState(range.from);
  const [customTo, setCustomTo] = useState(range.to);

  useEffect(() => {
    if (!urlFrom || !urlTo) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("from", range.from);
          next.set("to", range.to);
          return next;
        },
        { replace: true },
      );
    }
  }, [urlFrom, urlTo, range.from, range.to, setSearchParams]);

  useEffect(() => {
    setCustomFrom((prev) => (prev === range.from ? prev : range.from));
    setCustomTo((prev) => (prev === range.to ? prev : range.to));
  }, [range.from, range.to]);

  // The people this actor may filter by, narrowed to the SELECTED team: „Без
  // екип" offers the people who belong to no team, a selected team offers its own
  // members, and no team selection offers everybody in scope.
  const ownerCandidates = useMemo(() => {
    const members = scope.members || [];
    if (!teamLogical) return members;
    if (teamLogical === UNASSIGNED) return members.filter((m) => !m.team_id);
    return members.filter((m) => m.team_id === teamLogical);
  }, [scope.members, teamLogical]);

  // A selected person who is not part of the selected team is dropped instead of
  // silently describing another team's data. Runs after the scope has loaded.
  useEffect(() => {
    if (!(scope.members || []).length) return;
    const oid = searchParams.get("owner_id");
    if (!oid || oid === UNASSIGNED) return;
    const stillValid = ownerCandidates.some((m) => m.user_id === oid);
    if (!stillValid) {
      const next = new URLSearchParams(searchParams);
      next.delete("owner_id");
      next.delete("mine");
      setSearchParams(next, { replace: true });
    }
  }, [ownerCandidates, scope.members, searchParams, setSearchParams]);

  // A team_id left over from another organization (or one this actor may not
  // select) is dropped rather than sent to the API.
  useEffect(() => {
    if (!urlTeam || urlTeam === UNASSIGNED) return;
    if (!(scope.teams || []).length) return;
    if (scope.teams.some((team) => team.id === urlTeam)) return;
    const next = new URLSearchParams(searchParams);
    next.delete("team_id");
    setSearchParams(next, { replace: true });
  }, [urlTeam, scope.teams, searchParams, setSearchParams]);

  // Resolve the OWNER parameter that we pass to the API. "mine" is the only
  // logical value that requires re-resolving to a UUID on each render.
  const ownerApiValue = useMemo(() => {
    if (ownerLogical === "mine") return user?.id || null;
    return ownerLogical;
  }, [ownerLogical, user?.id]);

  const translateError = useCallback(
    (err) => friendlyError(err, t("dashboard.error_load")),
    [t],
  );

  const {
    summary,
    funnel,
    trend,
    aging,
    byOwner,
    statuses,
    byTeam,
    workload,
    loading,
    refreshing,
    error,
    lastUpdatedAt,
    refresh,
  } = useDashboardData({
    orgId,
    from: range.from,
    to: range.to,
    owner: ownerApiValue,
    team: teamLogical,
    includeTeamReports: !ownOnly,
    translateError,
  });

  const pollFn = useCallback(() => refresh({ background: true }), [refresh]);
  useVisibleInterval(pollFn, REFRESH_MS);

  useEffect(() => {
    const onOrg = () => refresh({ background: false });
    window.addEventListener("quowada:orgChanged", onOrg);
    return () => window.removeEventListener("quowada:orgChanged", onOrg);
  }, [refresh]);

  const setUrlRange = useCallback(
    (from, to) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("from", from);
          next.set("to", to);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setOwnerLogical = useCallback(
    (val) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("owner_id");
          next.delete("mine");
          if (val === "mine") next.set("mine", "true");
          else if (val === UNASSIGNED) next.set("owner_id", UNASSIGNED);
          else if (val) next.set("owner_id", val);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  // Changing the team resets the person filter in the SAME navigation, so the
  // dashboard never renders one team with another team's person selected.
  const setTeamLogical = useCallback(
    (val) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("team_id");
          next.delete("owner_id");
          next.delete("mine");
          if (val) next.set("team_id", val);
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const applyPreset = useCallback(
    (id) => {
      const p = computePreset(id);
      if (!p) return;
      setCustomFrom(p.from);
      setCustomTo(p.to);
      setUrlRange(p.from, p.to);
    },
    [setUrlRange],
  );

  const applyCustomRange = useCallback(() => {
    if (!customFrom || !customTo || customTo < customFrom) return;
    setUrlRange(customFrom, customTo);
  }, [customFrom, customTo, setUrlRange]);

  const goToMetric = useCallback(
    (metric, extras = {}) => {
      const merged = { ...extras };
      // For the unassigned_current KPI we must NOT forward the owner filter
      // (see buildClickThroughParams — but be explicit here too).
      if (metric !== "unassigned_current" && ownerApiValue) {
        merged.owner = ownerApiValue;
      }
      if (teamLogical) merged.team = teamLogical;
      const qs = buildClickThroughParams(metric, range, merged);
      navigate(`/quotations?${qs.toString()}`);
    },
    [navigate, range, ownerApiValue, teamLogical],
  );

  const goToStatic = useCallback(
    (metric) => {
      // Static (current-state) metrics still respect the scope, except
      // ``unassigned_current`` which is definitionally unassigned-owner.
      const extras = {};
      if (metric !== "unassigned_current" && ownerApiValue) {
        extras.owner = ownerApiValue;
      }
      if (teamLogical) extras.team = teamLogical;
      const qs = buildClickThroughParams(metric, {}, extras);
      navigate(`/quotations?${qs.toString()}`);
    },
    [navigate, ownerApiValue, teamLogical],
  );

  // The status tiles are a CURRENT-STATE segment, so they open the quotations
  // list by status inside the same scope — never a period cohort.
  const goToStatus = useCallback(
    (statusKey) => {
      const qs = new URLSearchParams();
      qs.set("status", statusKey);
      if (ownerApiValue) qs.set("owner_id", ownerApiValue);
      if (teamLogical) qs.set("team_id", teamLogical);
      navigate(`/quotations?${qs.toString()}`);
    },
    [navigate, ownerApiValue, teamLogical],
  );

  const goToTrendBucket = useCallback(
    (point) => {
      if (!point?.period_start) return;
      const startIso = String(point.period_start).slice(0, 10);
      const endIso = bucketEndSofia(point.period_start, trend?.bucket);
      goToMetric("accepted_bucket", {
        period_start: startIso,
        period_end: endIso,
      });
    },
    [goToMetric, trend?.bucket],
  );

  const goToOwnerRow = useCallback(
    (ownerUserId) => {
      // Open /quotations filtered by the row's owner and the current period.
      const qs = new URLSearchParams();
      qs.set("from", range.from);
      qs.set("to", range.to);
      if (ownerUserId === null) qs.set("owner_id", UNASSIGNED);
      else if (ownerUserId) qs.set("owner_id", ownerUserId);
      if (teamLogical) qs.set("team_id", teamLogical);
      navigate(`/quotations?${qs.toString()}`);
    },
    [navigate, range, teamLogical],
  );

  const goToTeamRow = useCallback(
    (teamId) => {
      const qs = new URLSearchParams();
      qs.set("team_id", teamId || UNASSIGNED);
      navigate(`/quotations?${qs.toString()}`);
    },
    [navigate],
  );

  const goToWorkloadRow = useCallback(
    (row) => {
      const qs = new URLSearchParams();
      if (row?.owner_user_id) qs.set("owner_id", row.owner_user_id);
      else qs.set("owner_id", UNASSIGNED);
      qs.set("team_id", row?.team_id || UNASSIGNED);
      navigate(`/quotations?${qs.toString()}`);
    },
    [navigate],
  );

  const pk = summary?.period_kpis || null;
  const ck = summary?.current_kpis || null;

  const trendSubtitle = useMemo(() => {
    if (!trend?.bucket) return "";
    return t(`dashboard.trend_bucket_${trend.bucket}`);
  }, [trend?.bucket, t]);

  const periodLabel = `${fmtSofiaDate(range.from)} – ${fmtSofiaDate(range.to)}`;

  return (
    <Layout
      title={t("dashboard.title")}
      actions={
        <Link className="qw-btn primary" to="/quotations/new" data-testid="btn-new-quotation">
          + {t("quotations.new")}
        </Link>
      }
    >
      <div className="h1">{t("dashboard.title")}</div>
      <div className="lead">{t("dashboard.subtitle")}</div>
      <div style={{ height: 16 }} />

      <DashboardScopeBar
        range={range}
        customFrom={customFrom}
        customTo={customTo}
        setCustomFrom={setCustomFrom}
        setCustomTo={setCustomTo}
        applyPreset={applyPreset}
        applyCustomRange={applyCustomRange}
        refreshing={refreshing}
        lastUpdatedAt={lastUpdatedAt}
        scope={scope}
        teamValue={ownOnly ? null : urlTeam}
        onTeamChange={setTeamLogical}
        ownerValue={ownerLogical}
        onOwnerChange={setOwnerLogical}
        ownerCandidates={ownerCandidates}
        currentUserId={user?.id || null}
        onRefresh={() => refresh({ background: true })}
      />

      {error && (
        <div
          className="qw-card"
          data-testid="dashboard-error"
          style={{ borderColor: "#fecaca", color: "#7f1d1d", background: "#fef2f2" }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{error}</div>
          <button
            className="qw-btn sm"
            onClick={() => refresh({ background: false })}
            data-testid="dashboard-retry"
          >
            {t("dashboard.retry")}
          </button>
        </div>
      )}

      <SectionTitle
        title={t("dashboard.section_period")}
        note={`${periodLabel} · ${t("dashboard.section_period_help")}`}
        testid="section-period-kpis"
      />
      <PeriodKpiGrid pk={pk} loading={loading} onMetricClick={goToMetric} />

      <SectionTitle
        title={t("dashboard.section_current_state")}
        note={t("dashboard.section_current_state_help")}
        testid="section-current-kpis"
      />
      <CurrentKpiGrid
        ck={ck}
        loading={loading}
        onMetricClick={goToStatic}
        ownerFilterActive={
          ownOnly || (!!ownerApiValue && ownerApiValue !== UNASSIGNED)
        }
      />
      <div style={{ height: 16 }} />
      <DashboardStatusPanel
        statuses={statuses}
        loading={loading}
        onStatusClick={goToStatus}
      />

      {!ownOnly && (
        <>
          <SectionTitle
            title={t("dashboard.section_by_team")}
            note={t("dashboard.scope_current_note")}
            testid="section-by-team"
          />
          <div className="qw-chart-card" data-testid="dashboard-by-team-card">
            <DashboardByTeam
              rows={byTeam?.rows || []}
              loading={loading}
              onRowClick={goToTeamRow}
            />
          </div>

          <SectionTitle
            title={t("dashboard.section_workload")}
            note={t("dashboard.scope_current_note")}
            testid="section-workload"
          />
          <div className="qw-chart-card" data-testid="dashboard-workload-card">
            <DashboardWorkload
              rows={workload?.rows || []}
              loading={loading}
              onRowClick={goToWorkloadRow}
            />
          </div>
        </>
      )}

      <SectionTitle
        title={`${t("dashboard.section_funnel")} · ${t("dashboard.section_trend")}`}
        note={`${periodLabel} · ${t("dashboard.scope_period_note")}`}
        testid="section-funnel-trend"
      />
      <div className="qw-grid charts">
        <div className="qw-chart-card" data-testid="dashboard-funnel-card">
          <div className="head">
            <div className="h">{t("dashboard.section_funnel")}</div>
            <div className="sub">{periodLabel}</div>
          </div>
          {loading || !funnel ? (
            <ChartSkeleton />
          ) : (
            <DashboardFunnel stages={funnel.stages} onSegmentClick={goToMetric} />
          )}
        </div>
        <div className="qw-chart-card" data-testid="dashboard-trend-card">
          <div className="head">
            <div className="h">{t("dashboard.section_trend")}</div>
            <div className="sub">{trendSubtitle}</div>
          </div>
          {loading || !trend ? (
            <ChartSkeleton />
          ) : (
            <DashboardAcceptedTrend
              data={trend.points}
              bucket={trend.bucket}
              onBarClick={goToTrendBucket}
            />
          )}
        </div>
      </div>

      <SectionTitle
        title={t("dashboard.section_secondary")}
        testid="section-secondary"
      />
      <div className="qw-chart-card" data-testid="dashboard-aging-card">
        <div className="head">
          <div className="h">{t("dashboard.section_aging")}</div>
          <div className="sub">{t("dashboard.scope_current_note")}</div>
        </div>
        {loading || !aging ? (
          <ChartSkeleton />
        ) : (
          <DashboardAging buckets={aging.buckets} onRowClick={goToStatic} />
        )}
      </div>

      {!ownOnly && (
        <>
          <div style={{ height: 16 }} />
          <div className="qw-chart-card" data-testid="dashboard-by-owner-card">
            <div className="head">
              <div className="h">{t("dashboard.section_by_owner")}</div>
              <div className="sub">{periodLabel}</div>
            </div>
            <DashboardByOwner
              rows={byOwner?.rows || []}
              loading={loading}
              onRowClick={goToOwnerRow}
            />
          </div>
        </>
      )}

      <div style={{ height: 24 }} />
    </Layout>
  );
}
